import { TemplateElement, html, defineElement, when, unsafeHTML, classMap } from '@webtides/element-js';
import style from './hero-image-text.css';
import '../../../button/button.js';
import '../../../chip/chip.js';
import '../../../icon/icon.js';
import { responsiveImage } from '../../../../directives/images.js';

/**
 * @typedef {Object} ImageLabelProperties
 * @property {string} title
 * @property {ImageProperties} image
 * @property {string} icon
 * @property {string} x
 * @property {string} y
 */

/**
 * @property {string} kicker
 * @property {string} chipText
 * @property {string} chipVariant
 * @property {string} headline
 * @property {string} text
 * @property {string} buttonSubline
 * @property {LinkProperties[]} links
 * @property {ImageProperties} image
 * @property {string} backgroundColor
 * @property {ImageProperties} backgroundImage
 * @property {ImageLabelProperties[]} imageLabels
 */
export default class HeroImageText extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            kicker: '',
            chipText: '',
            chipVariant: 'primary',
            headline: '',
            text: '',
            links: [],
            buttonSubline: '',
            icon: '',
            image: undefined,
            backgroundColor: '', // 'green-medium' || 'green-dark' || 'violet-medium' || 'violet-dark'
            backgroundImage: undefined,
            imageLabels: [],
        };
    }

    connected() {}

    template() {
        const hasBackgroundImage = !!this.backgroundImage?.srcset;
        const hasBackground = hasBackgroundImage || !!this.backgroundColor;
        // TODO: use /* mdx/sys/font/flex/Large/Headline1_Medium */ for headline

        const backgroundClasses = {
            'bg-green-medium': this.backgroundColor === 'green-medium',
            'bg-green-dark': this.backgroundColor === 'green-dark',
            'bg-violet-medium': this.backgroundColor === 'violet-medium',
            'bg-violet-dark': this.backgroundColor === 'violet-dark',
        };

        return html`
            <div class="relative">
                ${when(
                    this.backgroundColor,
                    html`<div
                        class="absolute inset-0 w-full h-full -translate-y-208 @lg:translate-y-0  ${classMap(
                            backgroundClasses,
                        )}"
                    ></div>`,
                )}
                ${when(
                    hasBackgroundImage,
                    responsiveImage(
                        this.backgroundImage,
                        'absolute lazyload inset-0 w-full h-full object-cover object-left-top @lg:pb-0 pb-208',
                        false,
                        'auto',
                        '',
                        'eager',
                    ),
                )}
                <div
                    class="relative grid @lg:auto-rows-auto grid-cols-1 @lg:grid-cols-2 items-center gap-fix-m @lg:gap-x-fix-3xl py-flex-m container container-lg"
                >
                    <div class="relative z-10 row-span-1 @lg:row-start-1 @lg:self-end">
                        ${when(
                            this.chipText,
                            html`
                                <mb-chip
                                    size="lg"
                                    variant="${this.chipVariant}"
                                    text="${this.chipText}"
                                    class="mb-fix-2xs"
                                ></mb-chip>
                            `,
                        )}
                        ${when(!this.chipText && this.kicker, html` <div class="mb-8">${this.kicker}</div> `)}
                        <div
                            class="${when(hasBackground, 'text-white', 'text-primary ')}"
                            style="${when(hasBackground, '--checkmark-color: white', '')}"
                        >
                            <h1 class="text-balance font-medium text-[44px] @lg:text-[60px]">
                                ${unsafeHTML(this.headline)}
                            </h1>
                            ${when(
                                this.text,
                                html`
                                    <div class="richtext fix-body1-medium mt-fix-s mb-0">${unsafeHTML(this.text)}</div>
                                `,
                            )}
                        </div>
                    </div>
                    <div class="relative z-20 row-span-1 @lg:row-span-2 @lg:row-start-1">
                        ${when(
                            this.image?.srcset,
                            responsiveImage(
                                this.image,
                                'aspect-video @lg:aspect-[4/3] object-cover rounded-[var(--mdx-sys-border-radius-default)] @lg:rounded-[var(--mdx-sys-border-radius-md)]',
                                false,
                                'auto',
                                '(min-width: 1440px) 650px, (min-width: 1024px) 45vw, 100vw',
                                'eager',
                            ),
                        )}
                        ${when(
                            this.imageLabels?.length > 0,
                            html`${this.imageLabels.map((label) => {
                                const hasImage = !!label.image?.src;
                                const hasIcon = label.icon !== '' && label.icon;
                                return html`
                                    <div
                                        class="label ${label.position}-label bg-primary-subtle1 lg:px-fix-s px-fix-xs rounded-[var(--mdx-sys-border-radius-default)] max-w-[160px] ${when(
                                            hasImage,
                                            'py-fix-2xs',
                                            'py-fix-s',
                                        )}"
                                        style="box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.15); --distance: ${label.distance}%; --shift:${label.shift}%;}"
                                    >
                                        ${when(
                                            hasImage,
                                            html` <img
                                                src="${label.image?.src}"
                                                class="-mt-fix-m pb-fix-xs mx-auto"
                                                alt="${label.image?.alt}"
                                            />`,
                                        )}
                                        ${when(
                                            !hasImage && hasIcon,
                                            html`
                                                <mb-icon
                                                    size="inherit"
                                                    name="${label.icon}"
                                                    class="h-32 size-32 object-cover mx-auto lg:py-fix-2xs py-fix-3xs lg:mb-fix-2xs mb-fix-3xs"
                                                ></mb-icon>
                                            `,
                                        )}

                                        <div
                                            class="richtext md:text-center text-green-medium ${when(
                                                !hasImage && !hasIcon,
                                                'has-no-image',
                                                '',
                                            )}"
                                        >
                                            ${unsafeHTML(label.title)}
                                        </div>
                                    </div>
                                `;
                            })}`,
                        )}
                    </div>
                    <div class="relative z-10 row-span-1 @lg:row-start-2 @lg:self-start">
                        ${when(
                            this.links?.length > 0,
                            html`<div
                                class="flex flex-col @lg:flex-row gap-fix-s ${when(
                                    hasBackground,
                                    '@lg:surface-dark',
                                    '',
                                )}"
                                data-surface="light"
                            >
                                ${this.links.map(
                                    (link) => html`
                                        <mb-button
                                            variant="${link.variant}"
                                            href="${link.url}"
                                            class="w-full text-center @lg:w-auto"
                                            >${link.text}</mb-button
                                        >
                                    `,
                                )}
                            </div>`,
                        )}
                        ${when(
                            this.buttonSubline,
                            html`<div
                                class="text-sm text-green-medium lg:!text-left text-center lg:text-white mt-fix-s w-full"
                            >
                                ${this.buttonSubline}
                            </div>`,
                        )}
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('mb-hero-image-text', HeroImageText);
